import { useMemo, useRef, useState, type ReactNode } from "react";
import { ScrollView, StyleSheet, Text, View, type TextInput } from "react-native";
import { useTranslation } from "react-i18next";
import {
  catalogBySlug,
  coerceLocale,
  modelDayName,
  modelDaysFor,
  modelRowAt,
  modelRowExercise,
  pickLocalizedText,
  PROGRAM_FOCUS,
  SESSION_MINUTES,
  type BuildReasonCode,
  type EffortScale,
  type Exercise,
  type ModelLevel,
  type ModelOptionChoices,
  type NewProgram,
  type ProgramFocus,
  type ProgramModel,
  type SessionMinutes,
} from "@heraion/shared";

import { difficultyLabel } from "../Library/difficultyLabel";
import {
  followDefaults,
  modelAxes,
  PROGRAM_NAME_MAX,
  variantName,
  type FollowChoices,
} from "../Library/modelCatalog";
import { buildErrorText } from "../ProgramEditor/buildErrorText";
import { Button } from "../primitives/Button";
import { DialogScroll } from "../primitives/Dialog";
import { ChipChoiceRow } from "../primitives/ChipChoiceRow";
import { FieldLabel } from "../primitives/FieldLabel";
import { TextField } from "../primitives/TextField";
import { DONE_FIELD_PROPS } from "../primitives/useFocusChain";
import { useExerciseNameBySlug } from "../Logger/exerciseName";
import type { UiTheme } from "../theme";
import { focusLabel, ModelFocusRow } from "./ModelFocusRow";
import { ModelWizardCut } from "./ModelWizardCut";
import { ModelWizardDay, type WizardRow } from "./ModelWizardDay";
import { ModelWizardGaps } from "./ModelWizardGaps";
import { ModelWizardOptions } from "./ModelWizardOptions";
import { makeSetupStyles } from "./setupStyles";
import { useModelWizardFit } from "./useModelWizardFit";
import { useModelWizardGaps } from "./useModelWizardGaps";

/** «9-10»: how many days a round lasts when `days` sessions run `perWeek`
 *  times a week. */
const roundDays = (days: number, perWeek: number): string => {
  const d = (days * 7) / perWeek;
  return Math.floor(d) === Math.ceil(d) ? String(d) : `${Math.floor(d)}-${Math.ceil(d)}`;
};

/** #472: Customise from the catalog. Only the axes that vary are asked, from
 *  the follow's defaults; «Create» becomes «Follow», «Back» «Cancel». F3:
 *  «Edit» beside Follow hands the same program to the editor. */
export type ModelFollow = {
  defaults: FollowChoices;
  /** The follow's write is in flight. */
  busy: boolean;
  /** The write failed: said under the button. */
  error: string | null;
  /** The exercise catalog is not read yet: Follow waits, or the copy would
   *  carry slugs as its exercise names. */
  waiting: boolean;
  /** The program these choices write, or null when the build refuses it. */
  renderPreview?: (program: NewProgram | null) => ReactNode;
  /** The program Follow would write, to the editor instead. Absent: no Edit. */
  onEdit?: (built: NewProgram) => void;
};

/** In follow mode the host's dialog already pads the screen. Follow and Edit
 *  share a row that wraps: a long label stacks the two, never clips. */
const local = StyleSheet.create({
  flush: { padding: 0 },
  pair: { flexDirection: "row", flexWrap: "wrap" },
  pairItem: { flexGrow: 1, flexShrink: 1, flexBasis: "auto", maxWidth: "100%" },
});

// A program model's wizard (#351, brief §15 row 33, flow 8).
// Choices: the name (#479: it takes the variant until it is typed), the
// level, the focus (#356, only when a row changes with it; else the model's
// own), the minutes per session, the sessions a week and the length, then
// the options the model admits (#479: a comeback, a muscle to bring up).
// Each offers only the model's own values.
// Then each day's rows: swap the exercise for an alternative, or take it out.
// Everything has a default, so after the name «Create» works in one tap.
// «Create» builds the draft (buildModelDraft) and hands its payload to the
// composer as the creation seed. It is never grey: tapped too early, it says
// what is missing, and takes the focus to the name when that is it. A
// program the build refuses says why too (#400).
export function ModelWizard({
  theme,
  model,
  catalog,
  effortScale,
  follow,
  onCreate,
  onBack,
}: {
  theme: UiTheme;
  /** No id: a model just saved has none until the server's list is read.
   *  With one, the program keeps its origin (#356). */
  model: Omit<ProgramModel, "id"> & { id?: string };
  catalog: ReadonlyArray<Exercise> | undefined;
  effortScale: EffortScale;
  follow?: ModelFollow;
  onCreate: (seed: NewProgram) => void;
  onBack: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = coerceLocale(i18n.language);
  const styles = useMemo(() => makeSetupStyles(theme), [theme]);
  const resolveName = useExerciseNameBySlug(catalog);
  const rounds = model.roundPattern.length;
  const bySlug = useMemo(() => catalogBySlug(catalog ?? []), [catalog]);
  // A control only for an axis with more than one value, or an option the model admits.
  const axes = useMemo(() => modelAxes(model, bySlug), [model, bySlug]);
  const hasFocus = axes.focus.length > 1;
  const start = follow?.defaults;
  // #479: null until typed; the name follows the choices till then.
  const [typed, setTyped] = useState<string | null>(null);
  const [level, setLevel] = useState<ModelLevel>(start?.level ?? axes.levels[0]!);
  // The chips start on the focus the model is written in.
  const [focus, setFocus] = useState<ProgramFocus>(start?.focus ?? model.focus ?? PROGRAM_FOCUS[0]);
  // No choice to make: the program takes the focus the model is written in.
  const atFocus = hasFocus ? focus : model.focus;
  const [minutes, setMinutes] = useState<SessionMinutes>(start?.minutes ?? followDefaults(model).minutes);
  const [perWeek, setPerWeek] = useState(start?.sessionsPerWeek ?? model.sessionsPerWeek[0]!);
  const [blocks, setBlocks] = useState(start?.blocks ?? model.blocks[0]!);
  const [options, setOptions] = useState<ModelOptionChoices>(start?.options ?? {});
  // A follow keeps the model's name: the Library's copy is found by it.
  const base = pickLocalizedText(model.name, lang);
  const variant = { level, sessionsPerWeek: perWeek, minutes, ...(atFocus ? { focus: atFocus } : {}), options };
  const name = typed ?? (follow ? base : variantName(base, axes, variant, t));
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [removed, setRemoved] = useState<ReadonlySet<string>>(() => new Set());
  const [open, setOpen] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  // #400: the program these choices made was refused. Create says why
  // until one of them changes: the reason is read against them.
  const [refused, setRefused] = useState<{ code: BuildReasonCode; from: readonly unknown[] } | null>(null);
  const nameRef = useRef<TextInput>(null);
  const scrollRef = useRef<ScrollView>(null);
  const dayY = useRef<Record<string, number>>({});

  // #413, #492 F3: what Create builds (rows put back, exercises added), read by the days and the time note too.
  const fitted = useModelWizardFit(model, removed, { level, focus: atFocus, minutes, perWeek, blocks, picks, effortScale, bySlug, lang, options });
  const { fit, build } = fitted;
  // #478: the days that run at perWeek. A row the fit or the athlete took out stays, struck, to come back.
  const days = useMemo(
    () =>
      modelDaysFor(fitted.full, perWeek).map((day) => ({
        id: day.id,
        name: modelDayName(day, atFocus, lang),
        rows: day.rows.flatMap((row): WizardRow[] => {
          const entry = modelRowAt(row, level, minutes, atFocus);
          return entry ? [{ id: row.id, exercises: entry.exercises, exercise: modelRowExercise(entry, picks[row.id]) }] : [];
        }),
      })),
    [fitted.full, perWeek, lang, level, minutes, atFocus, picks],
  );
  const gaps = useModelWizardGaps(fitted, { catalog, bySlug, days, lang, enabled: !follow });
  const toggleRemove = (id: string) => {
    // A row taken out has no alternatives to show.
    setOpen(null);
    // Put back, from the fit's cut or by hand: it stays, the fit cuts another (#492 F3).
    if (removed.has(id) || fitted.cut.has(id)) fitted.keep(id);
    if (fitted.cut.has(id)) return;
    setRemoved((old) => {
      const next = new Set(old);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  };
  const previewing = follow?.renderPreview !== undefined;
  const followBuilt = useMemo(() => (previewing ? build(name.trim()) : null), [previewing, build, name]);
  const empty = days.some((d) => d.rows.every((r) => removed.has(r.id)));
  const noName = name.trim().length === 0;
  const choices = [name, level, atFocus, minutes, perWeek, blocks, options, picks, removed, fitted.pinned, fitted.extras, effortScale, lang];
  const refusedNow = refused?.from.every((v, i) => v === choices[i]) ? refused.code : null;
  const why = !tried
    ? null
    : noName
      ? t("editorV3.tetrad.needName", { defaultValue: "Give the program a name first." })
      : empty
        ? t("editorV3.tetrad.needDay", { defaultValue: "Each day needs at least one exercise: put one back." })
        : refusedNow
          ? buildErrorText(refusedNow, t)
          : null;

  // Create, Follow and Edit: the same checks, then the program to `send`.
  const submit = (send: (seed: NewProgram) => void) => {
    if (noName || empty) {
      setTried(true);
      if (noName) nameRef.current?.focus();
      else {
        // The empty day says so in place: take it into view.
        const bare = days.find((d) => d.rows.every((x) => removed.has(x.id)));
        const y = bare ? dayY.current[bare.id] : undefined;
        if (y !== undefined) scrollRef.current?.scrollTo({ y, animated: true });
      }
      return;
    }
    const built = followBuilt ?? build(name.trim());
    if (built.ok) {
      send(built.program);
      return;
    }
    // Never mute: the reason in words, the English one for the logs.
    console.warn("[model wizard] build failed:", built.reason);
    setTried(true);
    setRefused({ code: built.code, from: choices });
  };
  const create = () => submit(onCreate);
  const onEdit = follow?.onEdit;

  const minutesHint =
    minutes === 45
      ? t("editorV3.model.minutes45", { defaultValue: "No optional exercises, and one set fewer on the accessories." })
      : minutes === 60
        ? t("editorV3.model.minutes60", { defaultValue: "No optional exercises." })
        : t("editorV3.model.minutes75", { defaultValue: "All the exercises." });

  // A choice with one option is not shown: its fixed value is said here.
  const fixed = [
    ...(model.sessionsPerWeek.length > 1
      ? []
      : [t("editorV3.tetrad.perWeekA11y", { count: perWeek, defaultValue: "{{count}} sessions a week" })]),
    ...(model.blocks.length > 1 && !follow
      ? []
      : [t("editorV3.tetrad.roundsA11y", { count: blocks * rounds, defaultValue: "{{count}} rounds" })]),
    ...(axes.levels.length > 1 ? [] : [difficultyLabel(level, t)]),
    ...(axes.focus.length === 1 ? [focusLabel(axes.focus[0]!, t)] : []),
  ];
  const fixedHint = fixed.length > 0 ? fixed.join(" · ") : null;

  const body = (
    <>
      <Text style={styles.title} accessibilityRole="header">
        {pickLocalizedText(model.name, lang)}
      </Text>
      <Text style={styles.hint}>{pickLocalizedText(model.description, lang)}</Text>
      {follow ? null : (
        <>
          <FieldLabel theme={theme}>{t("program.name", { defaultValue: "Name" })}</FieldLabel>
          <TextField
            ref={nameRef}
            theme={theme}
            touchFloor
            // The derived name is read whole: it wraps, up to three rows.
            // Return closes the keyboard, a name has no line break.
            multiline
            minRows={1}
            maxRows={3}
            {...DONE_FIELD_PROPS}
            value={name}
            onChangeText={setTyped}
            maxLength={PROGRAM_NAME_MAX}
            selectTextOnFocus
            accessibilityLabel={t("program.name", { defaultValue: "Name" })}
          />
          {typed === null ? (
            <Text style={styles.hint} accessibilityLiveRegion="polite" aria-live="polite">
              {t("editorV3.model.nameFollows", {
                defaultValue: "The name follows your choices until you type it.",
              })}
            </Text>
          ) : null}
        </>
      )}
      {axes.levels.length > 1 ? (
        <ChipChoiceRow
          theme={theme}
          choices={axes.levels}
          selected={level}
          onSelect={setLevel}
          label={(l) => difficultyLabel(l, t)}
          chipA11yLabel={(l) => difficultyLabel(l, t)}
          groupA11yLabel={t("editorV3.model.level", { defaultValue: "Level" })}
          title={t("editorV3.model.level", { defaultValue: "Level" })}
        />
      ) : null}
      {fixedHint ? <Text style={styles.hint}>{fixedHint}</Text> : null}
      {hasFocus ? (
        <ModelFocusRow theme={theme} focus={focus} onSelect={setFocus} hintStyle={styles.hint} />
      ) : null}
      {axes.minutes.length > 1 ? (
        <>
        <ChipChoiceRow
          theme={theme}
          choices={SESSION_MINUTES}
          selected={minutes}
          onSelect={setMinutes}
          label={String}
          chipA11yLabel={(n) => t("editorV3.model.minutesA11y", { count: n, defaultValue: "{{count}} minutes" })}
          groupA11yLabel={t("editorV3.model.minutes", { defaultValue: "Minutes per session" })}
          title={t("editorV3.model.minutes", { defaultValue: "Minutes per session" })}
        />
        <View>
          <Text style={styles.hint}>{minutesHint}</Text>
          <ModelWizardCut theme={theme} fit={fit} cutCount={fitted.cut.size} minutes={minutes} listed={!follow} hintStyle={styles.hint} />
        </View>
        </>
      ) : null}
      {axes.sessionsPerWeek.length > 1 ? (
        <ChipChoiceRow
          theme={theme}
          choices={axes.sessionsPerWeek}
          selected={perWeek}
          onSelect={setPerWeek}
          label={String}
          chipA11yLabel={(n) => t("editorV3.tetrad.perWeekA11y", { count: n, defaultValue: "{{count}} sessions a week" })}
          groupA11yLabel={t("editorV3.tetrad.perWeek", { defaultValue: "Sessions a week" })}
          title={t("editorV3.tetrad.perWeek", { defaultValue: "Sessions a week" })}
        />
      ) : null}
      {perWeek !== days.length ? (
        <Text style={styles.hint}>
          {t("editorV3.model.rotateHint", {
            count: days.length,
            length: roundDays(days.length, perWeek),
            defaultValue: "The {{count}} days take turns: a round lasts {{length}} days.",
          })}
        </Text>
      ) : null}
      {model.blocks.length > 1 && !follow ? (
        <ChipChoiceRow
          theme={theme}
          choices={model.blocks}
          selected={blocks}
          onSelect={setBlocks}
          label={(b) => String(b * rounds)}
          chipA11yLabel={(b) => t("editorV3.tetrad.roundsA11y", { count: b * rounds, defaultValue: "{{count}} rounds" })}
          groupA11yLabel={t("editorV3.tetrad.rounds", { defaultValue: "Rounds" })}
          title={t("editorV3.tetrad.rounds", { defaultValue: "Rounds" })}
        />
      ) : null}
      {blocks > 1 ? (
        <Text style={styles.hint}>
          {t("editorV3.model.blocksHint", {
            count: blocks,
            defaultValue: "The program runs {{count}} times: each time starts again a step higher.",
          })}
        </Text>
      ) : null}
      <ModelWizardOptions
        theme={theme}
        axes={axes.options}
        options={options}
        onChange={setOptions}
        ignoreOldMax={fitted.ignoreOldMax}
        hintStyle={styles.hint}
      />
      {(follow ? [] : days).map((day) => (
        <ModelWizardDay
          key={day.id}
          theme={theme}
          name={day.name}
          rows={day.rows}
          removed={removed}
          cut={gaps.cutNotes}
          open={open}
          nameOf={(slug) => resolveName(slug)}
          onLayoutY={(y) => {
            dayY.current[day.id] = y;
          }}
          onToggleRemove={toggleRemove}
          onToggleOpen={(id) => setOpen((o) => (o === id ? null : id))}
          onPick={(id, slug) => {
            setPicks((p) => ({ ...p, [id]: slug }));
            setOpen(null);
          }}
        />
      ))}
      {follow ? null : <ModelWizardGaps theme={theme} gaps={gaps} nameOf={resolveName} onAdd={fitted.add} />}
      {follow?.renderPreview?.(followBuilt?.ok ? followBuilt.program : null)}
    </>
  );
  const createProps = {
    theme,
    label: !follow
      ? t("program.create", { defaultValue: "Create" })
      : follow.busy
        ? t("library.following", { defaultValue: "Following…" })
        : t("program.follow", { defaultValue: "Follow" }),
    onPress: create,
    loading: follow?.busy === true,
    disabled: follow?.waiting === true,
  };
  const createAlone = <Button {...createProps} full />;
  // The gap clears the buttons' hitSlop when the row wraps.
  const followBesideEdit = (edit: (built: NewProgram) => void) => (
    <>
      <View style={[local.pair, { gap: theme.spacing.s5 }]}>
        <Button {...createProps} style={local.pairItem} />
        <Button
          theme={theme}
          variant="secondary"
          label={t("program.edit", { defaultValue: "Edit" })}
          onPress={() => submit(edit)}
          disabled={follow?.waiting === true || follow?.busy === true}
          style={local.pairItem}
        />
      </View>
      <Text style={styles.hint}>
        {t("library.modelEditHint", {
          defaultValue: "Edit opens the editor. The program is yours once you save.",
        })}
      </Text>
    </>
  );
  const actions = (
    <>
      {onEdit ? followBesideEdit(onEdit) : createAlone}
      {why || follow?.error || follow?.waiting ? (
        <Text style={styles.hint} accessibilityLiveRegion="polite" aria-live="polite">
          {why ??
            follow?.error ??
            t("library.modelWaitCatalog", { defaultValue: "Loading the exercises…" })}
        </Text>
      ) : null}
      <Button
        theme={theme}
        variant="ghost"
        label={follow ? t("program.cancel", { defaultValue: "Cancel" }) : t("editorV3.tetrad.back", { defaultValue: "Back" })}
        onPress={onBack}
        full
      />
    </>
  );
  // #472: in the widget the body scrolls and Follow stays in view, the
  // Dialog's own pattern (DialogScroll, actions pinned under it).
  if (follow) {
    return (
      <>
        <DialogScroll contentContainerStyle={[styles.container, local.flush]} keyboardShouldPersistTaps="handled">
          {body}
        </DialogScroll>
        <View style={[styles.container, local.flush]}>{actions}</View>
      </>
    );
  }
  return (
    <ScrollView ref={scrollRef} contentContainerStyle={styles.container} keyboardShouldPersistTaps="handled">
      {body}
      {actions}
    </ScrollView>
  );
}
